import * as React from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  TextField,
  Typography,
  Stack,
  Grid,
  Tab,
  Tabs,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Link, useHistory } from 'react-router-dom';
import { GENERAL_ROUTE } from '../../routes/const';
import { useDispatch, useSelector } from 'react-redux';
import { ROLES_SET } from '../../redux/constants/userConstants';
import { useEffect, useState } from 'react';
import { check, login } from '../../redux/actions/userActions';
import { getProfile } from '../../redux/actions/profile/profileActions';
import { useNCALayerClient } from '../../hooks/ecp/SignHooks';
import axios from 'axios';

const theme = createTheme();

const SignIn = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [tab, setTab] = useState(1); // 0 — клиент, 1 — пользователь НЦЭ
  const [client, ready] = useNCALayerClient();
  const [ecpLoginXml, setEcpLoginXml] = useState(null);
  const [ecpReady, setEcpReady] = useState(false);
  const [ecpMessage, setEcpMessage] = useState(null);
  const [ecpLoginSignedXml, setEcpLoginSignedXml] = useState(null);
  const [ecpLoading, setEcpLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const user = useSelector((state) => state.user);
  const { loading, success } = user;

  useEffect(() => {
    if (!client) return;

    client.onopen = function () {
      setEcpReady(true);
      setEcpMessage(null);
    };

    client.onclose = function () {
      setEcpReady(false);
      setEcpMessage('Ошибка соединения с NCALayer');
    };

    client.onmessage = function (msg) {
      setEcpLoginSignedXml(msg.responseObject);
    };
  }, [client]);

  useEffect(() => {
    (async () => {
      if (success) {
        await dispatch(getProfile());
        await dispatch(check());
        setEmail('');
        setPassword('');
        history.push(GENERAL_ROUTE);
      }
    })();
  }, [success, history]);

  // Автоматический вход после подписания ЭЦП для клиента
  useEffect(() => {
    if (ecpLoginSignedXml && tab === 0) { // tab === 0 это вкладка "Клиент"
      handleClientEcpLogin();
    }
  }, [ecpLoginSignedXml, tab]);

  // Cleanup при размонтировании компонента
  useEffect(() => {
    return () => {
      // Очищаем состояние при размонтировании
      setEcpLoading(false);
      setEcpMessage('');
    };
  }, []);

  // Подписывание XML
  const willSelectEcp = () => {
    const now = Date.now();
    const loginXml = `<login><time>${now}</time></login>`;
    setEcpLoginXml(loginXml);
    client.signXml('PKCS12', 'AUTHENTICATION', loginXml);
  };

  // 🔹 Вход по логину/паролю (для пользователя НЦЭ)
  const handleSubmit = async (event) => {
    event.preventDefault();
    await dispatch(login(email, password, ecpLoginXml, ecpLoginSignedXml));
  };

  // 🔹 Вход по ЭЦП (для клиента, /v2/authenticate)
  const handleClientEcpLogin = async () => {
    if (!ecpLoginSignedXml) {
      setEcpMessage('Подпишите XML перед входом');
      return;
    }
    
    setEcpLoading(true);
    setEcpMessage('');
    
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      const request = {};
      if (ecpLoginXml && ecpLoginSignedXml) {
        request.signature = {
          xml: ecpLoginXml,
          signedXml: ecpLoginSignedXml
        };
      }

      const { data } = await axios.post(
        `${baseUrl}/api/v2/authenticate`,
        request,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data?.token) {
        localStorage.setItem('token', data.token);
        if (data.roles) {
          localStorage.setItem('roles', JSON.stringify(data.roles));
        }
        if (data.employee) {
          localStorage.setItem('employee', JSON.stringify(data.employee));
        }
        
        // Сохраняем userInfo для check()
        const userInfo = {
          userId: data.userId,
          userName: data.userName || 'ECP User',
          roles: data.roles
        };
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        // Обновляем роли в Redux store
        dispatch({
            type: ROLES_SET,
            payload: data.roles
        });
        
        // Сразу редиректим без задержки
        window.location.href = GENERAL_ROUTE;
      } else {
        setEcpMessage('Ошибка: токен не получен');
      }
    } catch (e) {
      console.error(e);
      if (e.response?.status === 401) {
        setEcpMessage('Ошибка авторизации: неверная подпись ЭЦП');
      } else {
        setEcpMessage('Ошибка при входе по ЭЦП');
      }
    } finally {
      setEcpLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 5, height: 150, width: 150, bgcolor: 'secondary.main' }} src="/images/logo.svg" />
          <Typography component="h1" variant="h5">
            Авторизация
          </Typography>

          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{ mt: 3, mb: 2 }}
            variant="fullWidth"
          >
            <Tab label="Клиент" />
            <Tab label="Пользователь НЦЭ" />
          </Tabs>

          {loading && <CircularProgress />}
          {ecpMessage && (
            <Typography color="error" sx={{ mt: 1 }}>
              {ecpMessage}
            </Typography>
          )}

          {/* ----------- КЛИЕНТ ----------- */}
          {tab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Вход по ЭЦП
              </Typography>
              <Stack direction="column" alignItems="center" spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!ecpReady || ecpLoading}
                  onClick={willSelectEcp}
                >
                  {ecpLoading ? 'Выполняется вход...' : 'Выбрать ЭЦП'}
                </Button>
                
                {ecpLoginSignedXml && !ecpLoading && (
                  <Typography color="success.main" sx={{ mt: 1, textAlign: 'center' }}>
                    ✓ ЭЦП подписана. Выполняется автоматический вход...
                  </Typography>
                )}
              </Stack>
              {!ecpReady && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  Подключитесь к NCALayer перед входом.
                </Typography>
              )}

              <Grid container sx={{ mt: 2 }}>
                <Grid item>
                  <Link to="/sign-up">Ещё нет аккаунта? Регистрация</Link>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ----------- ПОЛЬЗОВАТЕЛЬ НЦЭ ----------- */}
          {tab === 1 && (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                id="email"
                label="Ваш логин"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                name="password"
                label="Пароль"
                type="password"
                id="password"
                autoComplete="current-password"
              />
              <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || (!email && !password && !ecpLoginSignedXml)}
                >
                  Войти
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={willSelectEcp}
                  disabled={!ecpReady}
                >
                  Выбрать ЭЦП
                </Button>
              </Stack>
              {!ecpReady && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  Подключитесь к NCALayer перед входом.
                </Typography>
              )}

              <Grid container sx={{ mt: 2 }}>
                <Grid item>
                  <Link to="/sign-up">Ещё нет аккаунта? Регистрация</Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default SignIn;
